// Single source of truth for the app's primary modes — labels, visibility, and
// the bucket each mode lives in (single-home rule per the style doctrine).
//
// Access lives in LeadWorkspaceHeaderAccess so the existing rules stay authoritative
// during the transition (we'll relocate that file in a later cleanup slice).
import type { AuthContextValue } from "@/contexts/AuthContext";
import {
  canAccessPrimaryMode,
  type PrimaryMode,
} from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { AnchorEntitlements } from "@/lib/entitlements/anchorsClient";
import { canAccessByAnchor } from "@/lib/entitlements/modeAnchorRequirement";
import type { BucketId } from "./bucketsRegistry";

export type ModeRegistryEntry = {
  id: PrimaryMode;
  label: string;
  bucketId: BucketId;
};

export const MODES_REGISTRY: ModeRegistryEntry[] = [
  { id: "leads", label: "Leads", bucketId: "sales" },
  // Order Intake — the role-scoped Sales-bucket landing for the
  // formal "submit this customer's order" / accept-hold-cancel review
  // / paperwork-check seats. Lifted out of the Manufacturing bucket
  // on 2026-06-15 per docs/products/turbo_sales.md §5 (the bucket
  // move) + handoff §8. Three role-scoped variants (Sales-rep /
  // Ops-reviewer / Mfg-accepter) render from one mode; the variant
  // picker lives in src/components/sales-intake/salesIntakeVariant.ts.
  // The Mfg-accepter variant renders today's
  // `ManufacturingOrderIntakePanel` content unchanged — same OI-2
  // service endpoints, same accept/hold/cancel UX.
  { id: "salesIntake", label: "Order Intake", bucketId: "sales" },
  // OI-5b slim subscriber portal (Q1) — not the full Sales shell.
  { id: "dealerPlaceOrder", label: "Place Order", bucketId: "dealer" },
  { id: "dealerOrderHistory", label: "Order History", bucketId: "dealer" },
  // Opportunity Engine (Phase 1, 2026-06-15). Reusable opportunity /
  // pricing / documents / generated-PDF workflow that replaces the
  // fillable PDF Red Iron sales process. Distinct from `salesIntake`
  // (which is the "submit captured lead into the manufacturer's intake
  // queue" flow). Spec: docs/GPT_To_Repo.md +
  // docs/design/sql_schema_opportunity.md.
  { id: "opportunities", label: "Opportunities", bucketId: "sales" },
  { id: "salesConsole", label: "Sales Console", bucketId: "sales" },
  { id: "adsAssistant", label: "Ads Assistant", bucketId: "sales" },
  { id: "training", label: "Training", bucketId: "sales" },
  { id: "aiPrompting", label: "AI Prompting", bucketId: "manager" },
  { id: "locationTools", label: "Location Tools", bucketId: "manager" },
  { id: "knowledgebase", label: "Knowledge Base", bucketId: "manager" },
  // Training Authoring re-homed out of the legacy `admin` bucket per
  // docs/design/admin_split_and_entitlements.md §5 + §7.1. Hosts the
  // roleplay-scenario catalog editor + org roleplay flags + KB suggest;
  // the corresponding consumer surface is the Sales-bucket Training
  // mode (Roleplay / Ask / Contacts / Reviews tabs).
  { id: "trainingAuthor", label: "Training Authoring", bucketId: "manager" },
  // Documents Editor — consolidated org documents + proposals + sign
  // templates + asset library (2026-07-06). Documents sub-mode hosts
  // browse/fill/export and inline authoring; Proposals sub-mode keeps
  // the opportunity PDF preset editor.
  { id: "formsAndFormats", label: "Documents Editor", bucketId: "manager" },
  // Ads Guidance carved out of the Sales-bucket Ads Assistant → Library
  // tab per docs/design/admin_split_and_entitlements.md §5 + §7.6. Hosts
  // the categories / templates / policies / post-tags / social mappings
  // authoring panels. The Sales-bucket `adsAssistant` mode keeps the
  // rep-facing ingestion + suggestion + feed-import + org-settings tabs.
  { id: "adsGuidance", label: "Ads Guidance", bucketId: "manager" },
  // Audience Data (MAS-3) — Sales Feed CSV ingest + sale-fact list for
  // Google/Meta audiences. Manager home next to Ads Guidance per
  // docs/planning/marketing_analytics_shared_sales_data.md Q1.
  { id: "audienceData", label: "Audience Data", bucketId: "manager" },
  { id: "coverageAssistant", label: "Coverage Assistant", bucketId: "coverage" },
  { id: "admin", label: "Admin", bucketId: "admin" },
  { id: "dashboard", label: "Dashboard", bucketId: "admin" },
  // Deals (mode id `operations`) — live deal instances + paperwork pulse.
  // Promoted out of legacy `admin` into top-level `ops` bucket per
  // docs/design/admin_split_and_entitlements.md §3 + §6.2. Mode id stays
  // `operations` for persisted last-mode / TAB_ACCESS stability; label
  // is **Deals** (2026-08-07) so it reads as instances, not the bucket.
  { id: "operations", label: "Deals", bucketId: "ops" },
  // Deal Types — reusable product-type + sale-type archetypes (authoring).
  // docs/planning/deal_types_mode.md
  { id: "dealTypes", label: "Deal Types", bucketId: "ops" },
  // Ops Lookup (OL-1) — read-only Sales / Orders find surface.
  // docs/planning/ops_lookup_mode.md
  { id: "opsLookup", label: "Lookup", bucketId: "ops" },
  // Ops Obligations (OB-1) — utilities / internet / leases / mortgages.
  // docs/planning/ops_obligations_mode.md
  { id: "opsObligations", label: "Obligations", bucketId: "ops" },
  // RTO Terms re-homed out of Tenant Admin → Org Administration → RTO
  // Settings accordion per docs/design/admin_split_and_entitlements.md
  // §5 row 7 + §7.7. Hosts the RTO-provider-name editor (Sales Console
  // RTO calculator title) plus the term-row + state-overrides editor.
  // Sales Console pulls term rows from here; manufacturer-bundled
  // inheritance via the "Use manufacturer RTO terms" checkbox stays on
  // the same surface.
  { id: "rtoTerms", label: "RTO Terms", bucketId: "ops" },
  { id: "dailyChecklist", label: "Daily checklist", bucketId: "ops" },
  { id: "physicalInventory", label: "Physical inventory", bucketId: "ops" },
  { id: "dev", label: "Dev", bucketId: "platform" },
  { id: "platform", label: "Platform", bucketId: "platform" },
  // Manufacturing bucket - pilot modes (always visible to mfg-role users).
  // `mfgInventory` label was bumped from "Inventory" to "Inventory & Receive"
  // when the Receive panel merged into Inventory on 2026-06-02, then
  // dropped back to "Inventory" on the same day's polish slice once the
  // merged panel got Receive lines / Recipe count tabs (the panel still
  // owns receive flows, the user-facing nav just doesn't need to call
  // that out).
  { id: "mfgInventory", label: "Inventory", bucketId: "manufacturing" },
  { id: "mfgModels", label: "Models", bucketId: "manufacturing" },
  { id: "mfgPricing", label: "COGS", bucketId: "manufacturing" },
  { id: "mfgOverhead", label: "Overhead", bucketId: "manufacturing" },
  // The pre-2026-06-15 `mfgOrderIntake` mode (briefly user-labelled
  // "Order Processing" per dev-sync note 01f9cbc2) moved to the Sales
  // bucket as `salesIntake` per docs/products/turbo_sales.md §5. The
  // OI-2 service endpoints + the panel UI are unchanged; the
  // Mfg-accepter variant of the new mode renders the same content.
  { id: "mfgOrders", label: "Orders", bucketId: "manufacturing" },
  // D3l originally relabelled this mode "Supply Network" to leave room for
  // capacity / linked-dealer rollups, but the dealer roster ended up
  // landing as a separate Logistics → Dealers mode instead. Reverted to
  // "Plants" on 2026-06-03 per dev-sync note f743698b — same mode ID to
  // avoid FK / TAB_ACCESS / test churn.
  { id: "mfgPlants", label: "Plants", bucketId: "manufacturing" },
  // Units roster rehomed under Build pipeline (Units sub-tab) — no top-level mode.
  { id: "mfgBuildPipeline", label: "Build pipeline", bucketId: "manufacturing" },
  { id: "mfgLaborBilling", label: "Labor rates", bucketId: "manufacturing" },
  // Manufacturing bucket - scaffold modes (gated by VITE_MFG_SCAFFOLD_TABS;
  // hidden in pilot via canAccessPrimaryMode). See LeadWorkspaceHeaderAccess.
  { id: "mfgReports", label: "Reports", bucketId: "manufacturing" },
  { id: "mfgAdminHome", label: "Admin Home", bucketId: "manufacturing" },
  // Production bucket - the floor/efficiency view of manufacturing,
  // split out of the Manufacturing bucket per
  // docs/design/buckets_and_entitlements.md §2. mfgFulfill is the only
  // mode here today (scaffold; same VITE_MFG_SCAFFOLD_TABS gate). The
  // first Production-bucket feature slice graduates the build pipeline
  // kanban into a real landing mode for this bucket.
  { id: "mfgFulfill", label: "Fulfill", bucketId: "production" },
  // Audits — BOM parts checks + Time Study labor capture (shared mode).
  { id: "mfgAudits", label: "Audits", bucketId: "production" },
  // Materials + recipe inventory count (MIC) — Production owns count/
  // reconcile; Manufacturing → Inventory keeps catalog / on-hand / receive.
  { id: "mfgInventoryCount", label: "Inventory count", bucketId: "production" },
  { id: "mfgPurchasing", label: "Purchasing Assistant", bucketId: "production" },
  { id: "mfgLaborPay", label: "Labor pay", bucketId: "production" },
  { id: "mfgBuilderAssistant", label: "Builder Assistant", bucketId: "production" },
  { id: "mfgQcDashboard", label: "QC Dashboard", bucketId: "production" },
  // Logistics bucket - pilot modes (always visible to mfg-family roles
  // for now; see LOGISTICS_ROLES in LeadWorkspaceHeaderAccess.ts).
  // D3l originally added a `logisticsDealers` mode here for the dealer
  // network; the Plants↔Dealers union slice (2026-06-03) moved that
  // entire surface into Manufacturing → Plants as the canonical edit
  // home and removed the dedicated Logistics mode. See
  // docs/planning/plants_dealers_union.md.
  { id: "logisticsRoutes", label: "Routes", bucketId: "logistics" },
  { id: "logisticsCalendar", label: "Calendar", bucketId: "logistics" },
  { id: "logisticsCarriers", label: "Carriers", bucketId: "logistics" },
  // Fulfillment bucket - Slice 1 modes (capability-gated by
  // `auth.hasSalesFloor` in canAccessPrimaryMode after Slice 3; the
  // canonical mode list lives in `salesFloorCapability.ts`). Order
  // matches the bucket landing rule: Inventory first (lot manager's
  // "what's at our location"), then Intake forms (the nag queue),
  // then New inventory (the entry form). See
  // docs/planning/sales_fulfillment_bucket.md §4.
  { id: "fulfillmentInventory", label: "Inventory", bucketId: "fulfillment" },
  { id: "fulfillmentOrders", label: "Orders", bucketId: "fulfillment" },
  { id: "fulfillmentIntake", label: "Intake forms", bucketId: "fulfillment" },
  { id: "fulfillmentForms", label: "Forms", bucketId: "fulfillment" },
  { id: "fulfillmentEntry", label: "New inventory", bucketId: "fulfillment" },
  { id: "serviceDefinitions", label: "Service setup", bucketId: "service" },
  { id: "serviceBoard", label: "Board", bucketId: "service" },
  { id: "serviceRequests", label: "Inbound requests", bucketId: "service" },
  { id: "serviceScheduling", label: "Scheduling", bucketId: "service" },
  { id: "serviceMaterialsUsage", label: "Materials Usage", bucketId: "service" },
];

/** Single-glyph hint for each mode while the rail is collapsed or rendered as a bottom tab. */
export const MODE_GLYPH: Record<PrimaryMode, string> = {
  leads: "Le",
  // "Oi" — kept from the pre-bucket-move `mfgOrderIntake` glyph so the
  // collapsed-rail abbreviation stays stable for users who recognize
  // it from the Mfg-bucket era. No collision with other Sales modes.
  salesIntake: "Oi",
  dealerPlaceOrder: "Po",
  dealerOrderHistory: "Oh",
  // Opportunity Engine — "Oe" (Deals glyph is "Dl").
  // Opportunity-Engine without clashing.
  opportunities: "Oe",
  salesConsole: "Sc",
  training: "Tr",
  // Training Authoring uses "Ta" since "Tr" is the consumer-side Training mode.
  trainingAuthor: "Ta",
  // Documents Editor — "De" for the consolidated documents surface.
  formsAndFormats: "De",
  knowledgebase: "Kb",
  aiPrompting: "Ai",
  adsAssistant: "Ad",
  // Ads Guidance uses "Ag" since "Ad" is the rep-side Ads Assistant mode.
  adsGuidance: "Ag",
  // Audience Data — "Sd" (shared sales data). "Au" is mfgAudits.
  audienceData: "Sd",
  coverageAssistant: "Cv",
  locationTools: "Lt",
  // Deals (mode id operations) — "Dl" ("De" taken by formsAndFormats).
  operations: "Dl",
  dealTypes: "Dt",
  // Lookup — "Lk".
  opsLookup: "Lk",
  opsObligations: "Ob",
  // RTO Terms — sits in the Ops bucket alongside Deals / Deal Types.
  // "Rt" was already taken by `logisticsRoutes`; "Ro" is unambiguous
  // since no other "Ro..." mode exists.
  rtoTerms: "Ro",
  dailyChecklist: "Dc",
  physicalInventory: "Pi",
  dashboard: "Db",
  admin: "Am",
  dev: "Dv",
  platform: "Pl",
  // Manufacturing modes. "Pl" is taken by Platform so Plants uses "Pt".
  // Admin Home uses "Mh" to keep "Ad" available for Ads Assistant.
  mfgInventory: "In",
  mfgModels: "Md",
  mfgPricing: "Cg",
  mfgOverhead: "Oh",
  mfgOrders: "Or",
  mfgPlants: "Pt",
  mfgBuildPipeline: "Bp",
  mfgLaborBilling: "Lr",
  mfgFulfill: "Fu",
  mfgAudits: "Au",
  mfgInventoryCount: "Ic",
  mfgPurchasing: "Po",
  mfgLaborPay: "Lv",
  mfgBuilderAssistant: "Ba",
  mfgQcDashboard: "Qc",
  mfgReports: "Rp",
  mfgAdminHome: "Mh",
  // Logistics glyphs. "Rt" for Routes; "Ca" for Carriers (stops /
  // scheduling-board are still future surfaces in this same bucket).
  logisticsRoutes: "Rt",
  logisticsCalendar: "Cl",
  logisticsCarriers: "Ca",
  // Fulfillment glyphs. "Iv" for Inventory (avoids the "In" collision
  // with mfgInventory), "Ik" for Intake forms (avoids the "It" /
  // "Ir" awkwardness), "Ne" for New inventory.
  fulfillmentInventory: "Iv",
  fulfillmentOrders: "Od",
  fulfillmentIntake: "Ik",
  fulfillmentForms: "Fm",
  fulfillmentEntry: "Ne",
  serviceDefinitions: "Su",
  serviceBoard: "Bd",
  serviceRequests: "Rq",
  serviceScheduling: "Sh",
  serviceMaterialsUsage: "Mu",
};

export type VisibleModeButton = ModeRegistryEntry & { show: boolean };

function passesBothGates(
  modeId: PrimaryMode,
  auth: AuthContextValue,
  anchors: AnchorEntitlements | null | undefined
): boolean {
  if (!canAccessPrimaryMode(modeId, auth)) return false;
  return canAccessByAnchor(modeId, anchors ?? null);
}

/** Returns each registry entry with a `show` flag set per the user's effective access. */
export function getVisibleModes(
  auth: AuthContextValue,
  anchors?: AnchorEntitlements | null
): VisibleModeButton[] {
  return MODES_REGISTRY.map((m) => ({ ...m, show: passesBothGates(m.id, auth, anchors) }));
}

/** Same as `getVisibleModes` but filtered down to just the modes the user can see. */
export function getAccessibleModes(
  auth: AuthContextValue,
  anchors?: AnchorEntitlements | null
): ModeRegistryEntry[] {
  return MODES_REGISTRY.filter((m) => passesBothGates(m.id, auth, anchors));
}

/** Accessible modes inside a specific bucket, in registry order. */
export function getAccessibleModesInBucket(
  bucketId: BucketId,
  auth: AuthContextValue,
  anchors?: AnchorEntitlements | null
): ModeRegistryEntry[] {
  return MODES_REGISTRY.filter(
    (m) => m.bucketId === bucketId && passesBothGates(m.id, auth, anchors)
  );
}

export { canAccessPrimaryMode, type PrimaryMode };
